<script lang="ts">
  import LeaveRoomDialog from '#lib/features/room/LeaveRoomDialog.svelte';
  import RoomSettingsDialog from '#lib/features/room/settings/RoomSettingsDialog.svelte';

  import RoomOptionsMenu from './RoomOptionsMenu.svelte';
  import type { Component } from 'svelte';
  import type { RoomListItemView } from '#src/generated/protocol';
  import { resolve } from '$app/paths';
  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/state';
  import { i18n } from '#lib/i18n.js';
  import { isRoomSwitch } from '#lib/features/room/room-navigation.js';
  import { findRoomByPathId, roomPathParam, useRoomList } from '#lib/rooms/room-list.svelte.js';
  import { addUnread } from '#lib/rooms/spaces.js';
  import type { UnreadCount } from '#lib/rooms/unread.js';
  import { NO_UNREAD, roomUnread, type RoomUnread } from '#lib/rooms/unread.js';
  import {
    folderName,
    mergeSpaces,
    refsEqual,
    type DropInstruction,
    type LayoutRef,
    type SidebarFolder,
    type SidebarItem,
  } from '#lib/spaces/sidebar-layout.js';
  import {
    DIRECT_PATHS_KEY,
    HOME_PATHS_KEY,
    ROOMS_PATHS_KEY,
    saveSpacePath,
    savedSpacePaths,
    isJoinedRoomPath,
    spaceNavigationHref,
  } from './space-paths.js';
  import { preferences, setPreference } from '#lib/settings/preferences.svelte.js';
  import { cursorAnchor, type CursorAnchor } from '#lib/ui/cursor-anchor.js';
  import { createDragList, type DropState } from '#lib/ui/drag-list.js';
  import { longPress, mouseContextMenu } from '#lib/ui/long-press.svelte.js';
  import ActionMenu from '#lib/ui/primitives/ActionMenu.svelte';
  import ActionMenuItem from '#lib/ui/primitives/ActionMenuItem.svelte';
  import ActionMenuSeparator from '#lib/ui/primitives/ActionMenuSeparator.svelte';
  import Avatar from '#lib/ui/primitives/Avatar.svelte';
  import { toInitials } from '#lib/ui/primitives/initials.js';
  import Tooltip from '#lib/ui/primitives/Tooltip.svelte';
  import UnreadBadge from '#lib/ui/primitives/UnreadBadge.svelte';
  import { resolveUnreadBadge } from '#lib/ui/primitives/unread-badge.js';
  import '#lib/ui/primitives/nav-tab.css';
  import CaretUpIcon from 'phosphor-svelte/lib/CaretUpIcon';
  import ChecksIcon from 'phosphor-svelte/lib/ChecksIcon';
  import CompassIcon from 'phosphor-svelte/lib/CompassIcon';
  import FolderOpenIcon from 'phosphor-svelte/lib/FolderOpenIcon';
  import HashIcon from 'phosphor-svelte/lib/HashIcon';
  import HouseIcon from 'phosphor-svelte/lib/HouseIcon';
  import LinkIcon from 'phosphor-svelte/lib/LinkIcon';
  import MagnifyingGlassIcon from 'phosphor-svelte/lib/MagnifyingGlassIcon';
  import SpeakerHighIcon from 'phosphor-svelte/lib/SpeakerHighIcon';
  import PencilSimpleIcon from 'phosphor-svelte/lib/PencilSimpleIcon';
  import PlusIcon from 'phosphor-svelte/lib/PlusIcon';
  import UsersIcon from 'phosphor-svelte/lib/UsersIcon';
  import UserQuickTools from './UserQuickTools.svelte';

  type RailSection = 'home' | 'unspaced' | 'direct';

  type RailItem = {
    href: string;
    roomId?: string;
    activePrefix: string;
    label: string;
    icon?: Component;
    initial?: string;
    avatar?: string | null;
    navigateHref?: string;
    unread?: UnreadCount;
    dm?: boolean;
    section?: RailSection;
    badge?: boolean;
    inCall?: boolean;
  };

  interface Props {
    pathname?: string;
    spaces: readonly RoomListItemView[];
    spaceUnread?: ReadonlyMap<string, UnreadCount>;
    callSpaces?: ReadonlySet<string>;
    homeUnread?: UnreadCount;
    unspacedUnread?: UnreadCount;
    directRooms?: readonly RoomListItemView[];
    directUnread?: UnreadCount;
    unreadFor?: RoomUnread;
    mobile?: boolean;
    compact?: boolean;
    onNavigate?: (href: string) => void;
    layout?: readonly SidebarItem[];
    openFolders?: ReadonlySet<string>;
    onToggleFolder?: (folderId: string) => void;
    onRenameFolder?: (folder: SidebarFolder) => void;
    onUngroupFolder?: (folderId: string) => void;
    onMarkFolderRead?: (folder: SidebarFolder) => void;
    onRemoveFromFolder?: (roomId: string, folderId: string) => void;
    onReorder?: (source: LayoutRef, target: LayoutRef, instruction: DropInstruction) => void;
    onMarkSectionRead?: (section: RailSection) => void;
    pinnedSpaceIds?: ReadonlySet<string>;
    onUnpin?: (roomId: string) => void;
  }

  let {
    pathname = page.url.pathname,
    spaces,
    spaceUnread = new Map(),
    callSpaces = new Set(),
    homeUnread = NO_UNREAD,
    unspacedUnread = NO_UNREAD,
    directRooms = [],
    directUnread = NO_UNREAD,
    unreadFor = roomUnread,
    mobile = false,
    compact = false,
    onNavigate,
    layout = [],
    openFolders = new Set(),
    onToggleFolder,
    onRenameFolder,
    onUngroupFolder,
    onMarkFolderRead,
    onRemoveFromFolder,
    onReorder,
    onMarkSectionRead,
    pinnedSpaceIds = new Set(),
    onUnpin,
  }: Props = $props();

  const roomList = useRoomList();
  const uid = $props.id();

  function unreadId(key: string): string {
    return `${uid}-unread-${key}`;
  }

  function unreadLabel(count: UnreadCount | undefined): string | undefined {
    if (!count) return undefined;
    if (count.highlight > 0) return i18n.t('nav.unreadMentions', { count: count.highlight });
    if (count.unread > 0) return i18n.t('nav.unreadMessages', { count: count.unread });
    return count.marked ? i18n.t('nav.markedUnread') : undefined;
  }

  function describedBy(props: Record<string, unknown>, id: string): string {
    const own = props['aria-describedby'];
    return typeof own === 'string' ? `${own} ${id}` : id;
  }
  const homeRoot = resolve('/(app)/home');
  const roomsRoot = resolve('/(app)/rooms');
  const directRoot = resolve('direct');
  const sectionRoots = [
    [HOME_PATHS_KEY, homeRoot],
    [ROOMS_PATHS_KEY, roomsRoot],
    [DIRECT_PATHS_KEY, directRoot],
  ] as const;
  let spacePaths = $state(savedSpacePaths());
  let dragged = $state<LayoutRef | null>(null);
  let dropState = $state<DropState<LayoutRef> | null>(null);

  let items = $derived<readonly RailItem[]>([
    ...(preferences.showHome
      ? [
          {
            href: homeRoot,
            activePrefix: '/home',
            navigateHref: sectionHref(HOME_PATHS_KEY, homeRoot),
            icon: HouseIcon,
            label: 'nav.home',
            unread: homeUnread,
            section: 'home',
            badge: false,
          } satisfies RailItem,
        ]
      : []),
    {
      href: roomsRoot,
      activePrefix: '/rooms',
      navigateHref: sectionHref(ROOMS_PATHS_KEY, roomsRoot),
      icon: preferences.showHome ? HashIcon : HouseIcon,
      label: 'nav.unspaced',
      unread: unspacedUnread,
      section: 'unspaced',
    },
    ...(preferences.showSearch
      ? [
          {
            href: resolve('/(app)/search'),
            activePrefix: '/search',
            icon: MagnifyingGlassIcon,
            label: 'common.searchMessages',
          } satisfies RailItem,
        ]
      : []),
    {
      href: directRoot,
      activePrefix: '/direct',
      navigateHref: sectionHref(DIRECT_PATHS_KEY, directRoot),
      icon: UsersIcon,
      label: 'common.directMessages',
      unread: directUnread,
      dm: true,
      section: 'direct',
    },
  ]);

  let spacesById = $derived(new Map(spaces.map((space) => [space.room_id, space])));
  let entries = $derived(
    mergeSpaces(
      layout,
      spaces.map((space) => space.room_id)
    )
  );
  let directItems = $derived<RailItem[]>(
    directRooms.map((room) => {
      const name = spaceName(room.name, room.room_id);
      const href = resolve('/(app)/direct/[roomId]', {
        roomId: roomPathParam(room, roomList.rooms),
      });

      return {
        href,
        activePrefix: href,
        roomId: room.room_id,
        initial: toInitials(name),
        avatar: room.avatar_url,
        label: name,
        unread: unreadFor(room),
        dm: true,
      };
    })
  );

  const createEntries = [
    { href: resolve('create-room'), label: 'common.createRoom', icon: PlusIcon },
    { href: resolve('create-space'), label: 'common.createSpace', icon: HouseIcon },
    {
      href: `${resolve('explore')}#explore-join-by-address`,
      label: 'nav.joinWithAddress',
      icon: LinkIcon,
    },
    { href: resolve('explore'), label: 'nav.explore', icon: CompassIcon },
  ] as const;
  let createOpen = $state(false);
  let createAnchor = $state.raw<HTMLElement | null>(null);

  function openCreateMenu(event: MouseEvent): void {
    if (event.currentTarget instanceof HTMLElement) createAnchor = event.currentTarget;
    createOpen = true;
  }

  function navigateTo(href: string): void {
    if (onNavigate) onNavigate(href);
    // eslint-disable-next-line svelte/no-navigation-without-resolve -- every rail href is built with resolve()
    else void goto(href);
  }

  function under(path: string, root: string): boolean {
    return path === root || path.startsWith(`${root}/`);
  }

  function decodedSegment(path: string, root: string): string | null {
    if (!path.startsWith(`${root}/`)) return null;
    const segment = path.slice(root.length + 1);
    if (segment.includes('/')) return null;
    try {
      return decodeURIComponent(segment);
    } catch {
      return null;
    }
  }

  function samePathRoom(previous: string | null, current: string): boolean {
    if (previous === null) return false;
    const idOf = (pathId: string) => findRoomByPathId(roomList.rooms, pathId)?.room_id ?? pathId;
    return idOf(previous) === idOf(current);
  }

  function tabHref(root: string, savedPath: string | undefined, fallback: string): string {
    if (!mobile) return spaceNavigationHref(root, savedPath, fallback);
    const joined = (pathId: string) => findRoomByPathId(roomList.rooms, pathId)?.state === 'joined';
    return savedPath !== undefined && isJoinedRoomPath(root, savedPath, joined) ? savedPath : root;
  }

  function sectionHref(key: string, root: string): string {
    return tabHref(root, spacePaths[key], root);
  }

  function spaceName(name: string | null, roomId: string): string {
    return name ?? roomId;
  }

  function outlined(item: RailItem): boolean {
    return item.icon !== undefined || item.dm === true;
  }

  let displayAnchor = $state.raw<CursorAnchor | null>(null);
  let displayOpen = $state(false);

  function openDisplayMenu(event: MouseEvent): void {
    const target = event.target;
    if (mobile) return;
    if (target instanceof Element && target.closest('a, button, [role="button"]')) return;

    event.preventDefault();
    displayAnchor = cursorAnchor(event);
    displayOpen = true;
  }

  const displayToggles = [
    { key: 'showUnreadCounts', label: 'settings.showUnreadCounts' },
    { key: 'badgeCountDMsOnly', label: 'settings.badgeCountDMsOnly' },
    { key: 'showPingCounts', label: 'settings.showPingCounts' },
  ] as const;
  const viewToggles = [
    { key: 'showHome', label: 'settings.showHome' },
    { key: 'uniformIcons', label: 'settings.uniformIcons' },
  ] as const;

  let contextSpace = $state<RoomListItemView | null>(null);
  let contextAnchor = $state.raw<CursorAnchor | null>(null);
  let contextOpen = $state(false);
  let sectionMenu = $state.raw<{ section: RailSection; unread: UnreadCount; dm: boolean } | null>(
    null
  );
  let sectionAnchor = $state.raw<CursorAnchor | null>(null);
  let sectionOpen = $state(false);
  let folderMenu = $state.raw<SidebarFolder | null>(null);
  let folderAnchor = $state.raw<CursorAnchor | null>(null);
  let folderOptionsOpen = $state(false);
  let contextFolderId = $state<string | null>(null);
  let settingsRoomId = $state<string | null>(null);
  let leaveRoomId = $state<string | null>(null);

  let settingsRoom = $derived(spaces.find((space) => space.room_id === settingsRoomId) ?? null);
  let leaveRoom = $derived(spaces.find((space) => space.room_id === leaveRoomId) ?? null);

  function openSpaceContextMenu(event: MouseEvent, roomId: string): void {
    const space = spacesById.get(roomId);
    if (space === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    contextSpace = space;
    contextFolderId = null;
    contextAnchor = cursorAnchor(event);
    contextOpen = true;
  }

  function entryRef(item: SidebarItem): LayoutRef {
    return item.kind === 'space'
      ? { kind: 'space', room_id: item.room_id, folder_id: null }
      : { kind: 'folder', folder_id: item.id };
  }

  let contextIndex = $derived.by(() => {
    const space = contextSpace;
    if (space === null) return -1;
    return entries.findIndex((entry) => entry.kind === 'space' && entry.room_id === space.room_id);
  });

  function moveContextSpace(direction: 'up' | 'down'): void {
    if (contextSpace === null || contextIndex === -1) return;
    const targetIndex = direction === 'up' ? contextIndex - 1 : contextIndex + 1;
    if (targetIndex < 0 || targetIndex >= entries.length) return;

    onReorder?.(
      { kind: 'space', room_id: contextSpace.room_id, folder_id: null },
      entryRef(entries[targetIndex]),
      direction === 'up' ? 'above' : 'below'
    );
  }

  function openSectionMenu(event: MouseEvent, item: RailItem, section: RailSection): void {
    event.preventDefault();
    event.stopPropagation();
    sectionMenu = { section, unread: item.unread ?? NO_UNREAD, dm: item.dm ?? false };
    sectionAnchor = cursorAnchor(event);
    sectionOpen = true;
  }

  function openFolderMenu(event: MouseEvent, folder: SidebarFolder): void {
    event.preventDefault();
    event.stopPropagation();
    folderMenu = folder;
    folderAnchor = cursorAnchor(event);
    folderOptionsOpen = true;
  }

  function openRemoveMenu(event: MouseEvent, roomId: string, folderId: string): void {
    event.preventDefault();
    event.stopPropagation();
    const space = spacesById.get(roomId);
    if (space === undefined) return;
    contextSpace = space;
    contextFolderId = folderId;
    contextAnchor = cursorAnchor(event);
    contextOpen = true;
  }

  function spaceItem(roomId: string): RailItem | null {
    const space = spacesById.get(roomId);
    if (space === undefined) return null;

    const name = spaceName(space.name, space.room_id);
    const href = resolve('/(app)/space/[spaceId]', {
      spaceId: roomPathParam(space, roomList.rooms),
    });
    const lobby = resolve('/(app)/space/[spaceId]/lobby', {
      spaceId: roomPathParam(space, roomList.rooms),
    });
    const savedPath = spacePaths[space.room_id];

    return {
      href,
      activePrefix: href,
      roomId: space.room_id,
      navigateHref: tabHref(href, savedPath, lobby),
      initial: toInitials(name),
      avatar: space.avatar_url,
      label: name,
      unread: spaceUnread.get(space.room_id),
      inCall: callSpaces.has(space.room_id),
    };
  }

  function folderLabel(folder: SidebarFolder): string {
    return (
      folderName(folder, (roomId) => spacesById.get(roomId)?.name ?? null) ?? i18n.t('nav.folder')
    );
  }

  function knownContent(folder: SidebarFolder): string[] {
    return folder.content.filter((roomId) => spacesById.has(roomId));
  }

  function folderUnread(folder: SidebarFolder): UnreadCount {
    return folder.content.reduce(
      (total, roomId) => addUnread(total, spaceUnread.get(roomId) ?? NO_UNREAD),
      NO_UNREAD
    );
  }

  function folderActive(folder: SidebarFolder): boolean {
    return folder.content.some((roomId) => {
      const item = spaceItem(roomId);
      return item !== null && isActive(item);
    });
  }

  function folderOpen(folder: SidebarFolder): boolean {
    return openFolders.has(folder.id);
  }

  function isActive(item: RailItem): boolean {
    if (item.initial) {
      return pathname.startsWith(`${item.activePrefix}/`) || pathname === item.activePrefix;
    }

    return pathname.startsWith(item.activePrefix);
  }

  function navigate(item: RailItem, event: MouseEvent): void {
    const href = item.navigateHref ?? item.href;
    if (onNavigate) {
      onNavigate(href);
      return;
    }
    const plain =
      event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    if (href === item.href || !plain) return;
    event.preventDefault();
    if (new URL(href, page.url.href).pathname === page.url.pathname) return;
    // eslint-disable-next-line svelte/no-navigation-without-resolve -- every rail href is built with resolve()
    void goto(href, { replace: isRoomSwitch(href), state: { mobileDrawer: 'open' } });
  }

  function dropping(ref: LayoutRef, instruction: DropInstruction): boolean {
    return (
      dropState !== null && dropState.instruction === instruction && refsEqual(dropState.item, ref)
    );
  }

  function isDragged(ref: LayoutRef): boolean {
    return dragged !== null && refsEqual(dragged, ref);
  }

  const noAttachment = (): undefined => undefined;
  const dragList = createDragList<LayoutRef>(refsEqual);

  function dragSource(ref: LayoutRef) {
    return mobile
      ? noAttachment
      : dragList.draggable(ref, (next) => {
          dragged = next;
        });
  }

  function dropTarget(ref: LayoutRef, allowInto: boolean) {
    return mobile
      ? noAttachment
      : dragList.dropTarget(ref, {
          allowInto,
          onState: (next) => {
            dropState = next;
          },
          onDrop: (source, target, instruction) => {
            onReorder?.(source, target, instruction);
          },
        });
  }

  const monitor = dragList.autoScroll();
  let previousPath = '';

  afterNavigate(() => {
    const kept = [...new URLSearchParams(page.url.search)].filter(([key]) => key !== 'event');
    const search = new URLSearchParams(kept).toString();
    const path = `${page.url.pathname}${search ? `?${search}` : ''}${page.url.hash}`;
    const space = spaces.find((candidate) => {
      const href = resolve('/(app)/space/[spaceId]', {
        spaceId: roomPathParam(candidate, roomList.rooms),
      });
      return path === href || path.startsWith(`${href}/`);
    });
    const key = sectionRoots.find(([, root]) => under(path, root))?.[0] ?? space?.room_id;
    const spaceParam = page.params.spaceId;
    const bounced = spaceParam
      ? sectionRoots.find(([, root]) =>
          samePathRoom(decodedSegment(previousPath, root), spaceParam)
        )
      : undefined;
    previousPath = page.url.pathname;

    if (bounced) {
      spacePaths = { ...spacePaths, [bounced[0]]: bounced[1] };
      saveSpacePath(bounced[0], bounced[1]);
    }
    if (mobile && page.params.roomId === undefined) return;
    if (key === undefined || spacePaths[key] === path) return;

    spacePaths = { ...spacePaths, [key]: path };
    saveSpacePath(key, path);
  });
</script>

<!-- eslint-disable svelte/no-navigation-without-resolve -- every rail href is
     built with resolve() above; resolving again here would double the base path -->
<!-- eslint-disable @typescript-eslint/no-confusing-void-expression -- a local
     snippet types as returning void, and the rule reads every {@render} of one
     as a void expression in an expression position -->
{#snippet nothing()}{/snippet}

{#snippet unreadMark(count: UnreadCount | undefined, dm: boolean, id: string)}
  <UnreadBadge counts={count} {dm} {id} label={unreadLabel(count)} />
{/snippet}

{#snippet itemBody(item: RailItem, active: boolean)}
  {#if item.icon}
    <span class="icon" aria-hidden="true"><item.icon weight={active ? 'fill' : 'regular'} /></span>
  {:else}
    <Avatar
      class="space-initial"
      id={item.roomId ?? null}
      src={item.avatar}
      initials={item.initial}
      uniform
    />
  {/if}
  {#if item.badge !== false}
    {@render unreadMark(item.unread, item.dm ?? false, unreadId(item.roomId ?? item.href))}
  {/if}
  {#if item.inCall}
    <span class="call-mark" aria-hidden="true"><SpeakerHighIcon weight="fill" /></span>
  {/if}
{/snippet}

{#snippet itemLink(item: RailItem, props: Record<string, unknown>, held: boolean)}
  {@const active = isActive(item)}
  <a
    {...props}
    class={[
      'rail-item nav-tab nav-tab-side selection-layer',
      { 'nav-tab-outlined': outlined(item), 'space-item': Boolean(item.initial) },
    ]}
    href={item.navigateHref ?? item.href}
    draggable={held ? 'false' : undefined}
    onclick={mobile
      ? (event) => {
          navigate(item, event);
        }
      : undefined}
    aria-label={i18n.t(item.label)}
    aria-describedby={describedBy(props, unreadId(item.roomId ?? item.href))}
    aria-current={active ? 'page' : undefined}
  >
    <span class="nav-tab-hit-area" aria-hidden="true"></span>
    {@render itemBody(item, active)}
  </a>
{/snippet}

{#snippet railItem(item: RailItem, held: boolean)}
  {#if mobile}
    {@render itemLink(item, {}, held)}
  {:else}
    {@const label = i18n.t(item.label)}
    {#snippet trigger({ props }: { props: Record<string, unknown> })}
      {@render itemLink(item, props, held)}
    {/snippet}
    <Tooltip {label} side="right" {trigger} />
  {/if}
{/snippet}

{#snippet createButton(props: Record<string, unknown>)}
  <button
    {...props}
    type="button"
    class="rail-item nav-tab nav-tab-side nav-tab-outlined selection-layer"
    aria-label={i18n.t('nav.add')}
    aria-haspopup="menu"
    aria-expanded={createOpen}
    onclick={openCreateMenu}
  >
    <span class="nav-tab-hit-area" aria-hidden="true"></span>
    <span class="icon" aria-hidden="true"
      ><PlusIcon weight={createOpen ? 'fill' : 'regular'} /></span
    >
  </button>
{/snippet}

{#snippet sectionItem(item: RailItem, section: RailSection)}
  <div
    class="rail-menu-anchor rail-section-anchor"
    role="presentation"
    oncontextmenu={mouseContextMenu((event) => {
      openSectionMenu(event, item, section);
    })}
    {@attach longPress({
      onPress: (event) => {
        openSectionMenu(event, item, section);
      },
    })}
  >
    {@render railItem(item, false)}
  </div>
{/snippet}

{#snippet spaceSlotBody(item: RailItem, ref: LayoutRef, folderId?: string)}
  <div
    class={[
      'rail-slot',
      {
        nested: folderId !== undefined,
        'drop-above': dropping(ref, 'above'),
        'drop-below': dropping(ref, 'below'),
        'drop-into': dropping(ref, 'into'),
        dragged: isDragged(ref),
      },
    ]}
    {@attach dragSource(ref)}
    {@attach dropTarget(ref, folderId === undefined)}
  >
    {@render railItem(item, !mobile)}
  </div>
{/snippet}

{#snippet spaceSlot(roomId: string, folderId?: string)}
  {@const item = spaceItem(roomId)}
  {#if item !== null}
    {@const ref = {
      kind: 'space',
      room_id: roomId,
      folder_id: folderId ?? null,
    } satisfies LayoutRef}
    {#if folderId === undefined}
      <div
        class="rail-menu-anchor"
        oncontextmenu={mouseContextMenu((event) => {
          openSpaceContextMenu(event, roomId);
        })}
        role="presentation"
        {@attach longPress({
          onPress: (event) => {
            openSpaceContextMenu(event, roomId);
          },
        })}
      >
        {@render spaceSlotBody(item, ref)}
      </div>
    {:else}
      <div
        class="rail-menu-anchor"
        role="presentation"
        oncontextmenu={mouseContextMenu((event) => {
          openRemoveMenu(event, roomId, folderId);
        })}
        {@attach longPress({
          onPress: (event) => {
            openRemoveMenu(event, roomId, folderId);
          },
        })}
      >
        {@render spaceSlotBody(item, ref, folderId)}
      </div>
    {/if}
  {/if}
{/snippet}

{#snippet folderTiles(folder: SidebarFolder)}
  <span class="folder-tiles" aria-hidden="true">
    {#each knownContent(folder) as roomId (roomId)}
      {@const space = spacesById.get(roomId)}
      <Avatar
        class="folder-tile"
        id={roomId}
        src={space?.avatar_url}
        name={spaceName(space?.name ?? null, roomId)}
        size="small"
        uniform
      />
    {/each}
  </span>
{/snippet}

{#snippet folderButton(folder: SidebarFolder, props: Record<string, unknown>)}
  <button
    {...props}
    type="button"
    class="rail-item folder-preview nav-tab nav-tab-side selection-layer"
    data-current={folderActive(folder) ? 'true' : undefined}
    aria-expanded="false"
    aria-label={i18n.t('nav.folderExpand', { name: folderLabel(folder) })}
    aria-describedby={describedBy(props, unreadId(folder.id))}
    onclick={() => {
      onToggleFolder?.(folder.id);
    }}
  >
    <span class="nav-tab-hit-area" aria-hidden="true"></span>
    {@render folderTiles(folder)}
    {@render unreadMark(folderUnread(folder), false, unreadId(folder.id))}
  </button>
{/snippet}

{#snippet closedFolder(folder: SidebarFolder)}
  {@const ref = { kind: 'folder', folder_id: folder.id } satisfies LayoutRef}
  <div
    class={[
      'rail-slot',
      {
        'drop-above': dropping(ref, 'above'),
        'drop-below': dropping(ref, 'below'),
        'drop-into': dropping(ref, 'into'),
        dragged: isDragged(ref),
      },
    ]}
    role="presentation"
    oncontextmenu={mouseContextMenu((event) => {
      openFolderMenu(event, folder);
    })}
    {@attach dragSource(ref)}
    {@attach dropTarget(ref, true)}
    {@attach longPress({
      onPress: (event) => {
        openFolderMenu(event, folder);
      },
    })}
  >
    {#if mobile}
      {@render folderButton(folder, {})}
    {:else}
      {@const label = folderLabel(folder)}
      {#snippet trigger({ props: triggerProps }: { props: Record<string, unknown> })}
        {@render folderButton(folder, triggerProps)}
      {/snippet}
      <Tooltip {label} side="right" {trigger} />
    {/if}
  </div>
{/snippet}

{#snippet openFolder(folder: SidebarFolder)}
  {@const ref = { kind: 'folder', folder_id: folder.id } satisfies LayoutRef}
  <div
    class={[
      'folder-open',
      { 'drop-above': dropping(ref, 'above'), 'drop-below': dropping(ref, 'below') },
    ]}
    {@attach dropTarget(ref, false)}
  >
    <div class="folder-card">
      <button
        type="button"
        class="folder-collapse"
        aria-expanded="true"
        aria-label={i18n.t('nav.folderCollapse', { name: folderLabel(folder) })}
        onclick={() => {
          onToggleFolder?.(folder.id);
        }}
        oncontextmenu={mouseContextMenu((event) => {
          openFolderMenu(event, folder);
        })}
        {@attach longPress({
          onPress: (event) => {
            openFolderMenu(event, folder);
          },
        })}
      >
        <CaretUpIcon weight="fill" />
      </button>
      {#each folder.content as roomId (roomId)}
        {@render spaceSlot(roomId, folder.id)}
      {/each}
    </div>
  </div>
{/snippet}

<div class="rail" role="presentation" oncontextmenu={mouseContextMenu(openDisplayMenu)}>
  <div class="rail-scroll" {@attach mobile ? noAttachment : monitor}>
    <ul class="rail-stack">
      {#each [...items, ...directItems] as item (item.roomId ?? item.href)}
        <li>
          {#if item.section !== undefined}
            {@render sectionItem(item, item.section)}
          {:else}
            {@render railItem(item, false)}
          {/if}
        </li>
      {/each}
    </ul>
    {#if entries.length > 0}
      <div class="rail-separator" role="separator"></div>
      <ul class="rail-stack">
        {#each entries as entry (entry.kind === 'space' ? entry.room_id : entry.id)}
          <li>
            {#if entry.kind === 'space'}
              {@render spaceSlot(entry.room_id)}
            {:else if knownContent(entry).length === 0}
              {@render nothing()}
            {:else if folderOpen(entry)}
              {@render openFolder(entry)}
            {:else}
              {@render closedFolder(entry)}
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
    <ul class="rail-stack">
      <li>
        {#if mobile}
          {@render createButton({})}
        {:else}
          {#snippet trigger({ props }: { props: Record<string, unknown> })}
            {@render createButton(props)}
          {/snippet}
          <Tooltip label={i18n.t('nav.add')} side="right" {trigger} />
        {/if}
      </li>
    </ul>
  </div>
  {#if !mobile && compact}
    <UserQuickTools compact />
  {/if}
</div>

{#if !mobile}
  <ActionMenu
    bind:open={displayOpen}
    label={i18n.t('nav.displayOptions')}
    class="rail-display-menu"
    anchor={displayAnchor}
    side="right"
    align="start"
    preventScroll={false}
  >
    {#each displayToggles as toggle (toggle.key)}
      {@const on = preferences[toggle.key]}
      <ActionMenuItem
        closeOnSelect={false}
        checked={on}
        onSelect={() => {
          setPreference(toggle.key, !on);
        }}
      >
        <span class="menu-check" aria-hidden="true">{on ? '✓' : ''}</span>
        <span>{i18n.t(toggle.label)}</span>
      </ActionMenuItem>
    {/each}

    <ActionMenuSeparator />

    {#each viewToggles as toggle (toggle.key)}
      {@const on = preferences[toggle.key]}
      <ActionMenuItem
        closeOnSelect={false}
        checked={on}
        onSelect={() => {
          setPreference(toggle.key, !on);
        }}
      >
        <span class="menu-check" aria-hidden="true">{on ? '✓' : ''}</span>
        <span>{i18n.t(toggle.label)}</span>
      </ActionMenuItem>
    {/each}
  </ActionMenu>
{/if}

<ActionMenu
  bind:open={createOpen}
  label={i18n.t('nav.add')}
  anchor={createAnchor}
  side="right"
  align="end"
>
  {#each createEntries as entry (entry.label)}
    <ActionMenuItem
      onSelect={() => {
        navigateTo(entry.href);
      }}
    >
      <entry.icon />
      {i18n.t(entry.label)}
    </ActionMenuItem>
  {/each}
</ActionMenu>

{#if sectionMenu}
  {@const menu = sectionMenu}
  <ActionMenu
    bind:open={sectionOpen}
    label={i18n.t('nav.listOptions')}
    anchor={sectionAnchor}
    side="right"
    align="start"
  >
    <ActionMenuItem
      disabled={resolveUnreadBadge(menu.unread, preferences, menu.dm) === null}
      onSelect={() => {
        onMarkSectionRead?.(menu.section);
      }}
    >
      <ChecksIcon />
      {i18n.t('common.markAsRead')}
    </ActionMenuItem>
  </ActionMenu>
{/if}

{#if folderMenu}
  {@const folder = folderMenu}
  <ActionMenu
    bind:open={folderOptionsOpen}
    label={i18n.t('nav.listOptions')}
    anchor={folderAnchor}
    side="right"
    align="start"
  >
    <ActionMenuItem
      onSelect={() => {
        onMarkFolderRead?.(folder);
      }}
    >
      <ChecksIcon />
      {i18n.t('common.markAsRead')}
    </ActionMenuItem>
    <ActionMenuSeparator />
    <ActionMenuItem
      onSelect={() => {
        onRenameFolder?.(folder);
      }}
    >
      <PencilSimpleIcon />
      {i18n.t('nav.folderRename')}
    </ActionMenuItem>
    <ActionMenuItem
      onSelect={() => {
        onUngroupFolder?.(folder.id);
      }}
    >
      <FolderOpenIcon />
      {i18n.t('nav.folderUngroup')}
    </ActionMenuItem>
  </ActionMenu>
{/if}

{#if contextSpace}
  <RoomOptionsMenu
    room={contextSpace}
    anchor={contextAnchor}
    align="start"
    side="right"
    bind:open={contextOpen}
    onSettings={(room: RoomListItemView) => {
      settingsRoomId = room.room_id;
    }}
    onLeave={(room: RoomListItemView) => {
      leaveRoomId = room.room_id;
    }}
    onMoveUp={mobile && contextIndex > 0
      ? () => {
          moveContextSpace('up');
        }
      : undefined}
    onMoveDown={mobile && contextIndex !== -1 && contextIndex < entries.length - 1
      ? () => {
          moveContextSpace('down');
        }
      : undefined}
    onRemoveFromFolder={contextFolderId !== null
      ? () => {
          if (contextSpace && contextFolderId !== null)
            onRemoveFromFolder?.(contextSpace.room_id, contextFolderId);
        }
      : undefined}
    onUnpin={onUnpin && pinnedSpaceIds.has(contextSpace.room_id)
      ? () => {
          if (contextSpace) onUnpin(contextSpace.room_id);
        }
      : undefined}
  />
{/if}

{#if settingsRoom}
  <RoomSettingsDialog
    open
    room={settingsRoom}
    onOpenChange={(next) => {
      if (!next) settingsRoomId = null;
    }}
  />
{/if}

{#if leaveRoom}
  <LeaveRoomDialog
    open
    room={leaveRoom}
    onOpenChange={(next) => {
      if (!next) leaveRoomId = null;
    }}
  />
{/if}

<style>
  .call-mark {
    align-items: center;
    background: var(--success-main);
    border: var(--border-width) solid var(--bg-container);
    border-radius: var(--radii-pill);
    color: var(--success-on-main);
    display: flex;
    justify-content: center;
    padding: var(--space-050);
    position: absolute;
    right: -0.25rem;
    top: -0.25rem;
    z-index: 1;
  }

  .call-mark :global(svg) {
    height: 0.625rem;
    width: 0.625rem;
  }

  .rail {
    background: var(--bg-container);
    border-right: var(--border-width) solid var(--bg-container-line);
    box-sizing: border-box;
    color: var(--bg-on-container);
    display: flex;
    flex: 0 0 var(--navigation-rail-width);
    flex-direction: column;
    min-height: 0;
    -webkit-touch-callout: none;
    user-select: none;
    width: var(--navigation-rail-width);
  }

  .rail-scroll {
    flex: 1;
    min-height: 0;
    overflow: hidden auto;
    scrollbar-width: none;
  }

  .rail-scroll::-webkit-scrollbar {
    display: none;
  }

  .rail-separator {
    background: var(--bg-container-line);
    block-size: var(--border-width);
    margin: 0 auto;
    width: 1.5rem;
  }

  .rail-stack {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: var(--space-300);
    list-style: none;
    margin: 0;
    padding: var(--space-300) 0 var(--space-200);
  }

  .icon {
    display: flex;
  }

  .icon :global(svg) {
    height: var(--icon-size-medium);
    width: var(--icon-size-medium);
  }

  .rail-menu-anchor {
    display: contents;
  }

  .rail-slot {
    position: relative;
  }

  .rail-slot.dragged {
    opacity: 0.4;
  }

  .rail-slot.drop-above::after,
  .rail-slot.drop-below::after,
  .folder-open.drop-above::after,
  .folder-open.drop-below::after {
    background: var(--primary-main);
    border-radius: var(--radius-pill);
    content: '';
    height: 2px;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
  }

  .rail-slot.drop-above::after,
  .folder-open.drop-above::after {
    top: -0.5rem;
  }

  .rail-slot.drop-below::after,
  .folder-open.drop-below::after {
    bottom: -0.5rem;
  }

  .rail-slot.nested.drop-above::after {
    top: -0.375rem;
  }

  .rail-slot.nested.drop-below::after {
    bottom: -0.375rem;
  }

  .rail-slot.drop-into :global(.rail-item) {
    outline: 2px solid var(--primary-main);
    outline-offset: 1px;
  }

  .folder-preview {
    border: 0;
    height: auto;
    min-height: 2.625rem;
    outline: var(--border-width) solid var(--bg-container-line);
    outline-offset: calc(var(--border-width) * -1);
    padding: var(--space-100);
  }

  .folder-tiles {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-050);
    place-content: center;
    width: 100%;
  }

  .folder-tiles :global(.folder-tile) {
    --avatar-size: 1rem;

    border-radius: var(--radii-300);
    font-size: calc(var(--font-size-small) * 0.6);
  }

  .folder-open {
    position: relative;
    width: var(--avatar-size-400);
  }

  .folder-card {
    align-items: center;
    background: transparent;
    border: var(--border-width) solid var(--bg-container-line);
    border-radius: var(--radii-500);
    display: flex;
    flex-direction: column;
    gap: var(--space-100);
    padding: var(--space-100) 0;
  }

  .folder-card .rail-slot.nested :global(.rail-item) {
    height: 2.125rem;
    width: 2.125rem;
  }

  .folder-collapse {
    align-items: center;
    background: none;
    border: 0;
    border-radius: var(--radius);
    color: inherit;
    cursor: pointer;
    display: flex;
    height: 2.125rem;
    justify-content: center;
    padding: 0;
    width: 2.125rem;
  }

  .folder-collapse:hover {
    background: var(--bg-container-hover);
  }

  .folder-collapse:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
  }
</style>
